import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { useViewCtx } from '../context/ViewContext'
import { HintDot } from '../help/HintDot'
import { speciesCaveat, useT } from '../i18n'
import { DepthSliderCompact } from '../maps/DepthSliderCompact'
import { SpeciesSelector } from '../models/SpeciesSelector'
import { useSpeciesInView } from '../species/useSpeciesInView'
import { dateBounds } from '../domain/limits'
import { useDomainLimits } from '../domain/useDomainLimits'

/**
 * Общие параметры над разделами «Карты» и «Аналитика»: дата · вид · глубина.
 * Значения живут в ViewContext и переживают переключение разделов. Глубина
 * гаснет, если активный слой/модель её не использует (SST/батиметрия/апвеллинг —
 * depthApplicable ставит активный раздел).
 *
 * Блок компактный: заголовок «Контекст» убран, подписи стоят в строке со своими
 * полями, глубиномер горизонтальный (фидбек владельца 2026-08-07). Панель
 * занимала столько высоты, что разделы под ней не помещались на экран.
 */
export function ContextBlock() {
  const t = useT()
  const { date, setDate, depth, setDepth, species, setSpecies, depthApplicable, depthNote } = useViewCtx()
  // Окно дат домена: календарь не предлагает дат, на которые сервер откажет.
  const dayBounds = dateBounds(useDomainLimits().period, new Date().toISOString().slice(0, 10))
  const { data } = useQuery({ queryKey: ['cabinet', 'species'], queryFn: () => cabinetApi.species() })
  // Список режется по кадру карты: в Балтике незачем предлагать тихоокеанскую
  // сайру. Оговорку о статусе модели ищем в ПОЛНОМ списке — выбранный вид
  // может стоять «вне района», и подпись под ним пропадать не должна.
  const speciesList = useMemo(() => data?.species ?? [], [data])
  const { inView, outOfView } = useSpeciesInView(species)
  // Оговорка о статусе модели выбранного вида. Стоит она ПОД списком, а не в
  // подсказке и не в описании вида: карту, опирающуюся на непроверенную
  // модель, рыбак должен видеть помеченной, ничего для этого не нажимая.
  const caveat = useMemo(
    () => speciesCaveat(speciesList.find(s => s.species_id === species)?.caveat),
    [speciesList, species],
  )

  return (
    <div className="space-y-3 border-b border-hull-hair px-3 pb-4 pt-3">
      <div className="flex items-center gap-2">
        <span className="eyebrow flex shrink-0 items-center gap-1.5">
          {t('context.date')}
          <HintDot id="date" />
        </span>
        <div className="flex-1 rounded-lg border border-hull bg-trench px-2 py-1 focus-within:border-sonar">
          <input
            type="date"
            value={date}
            min={dayBounds?.min}
            max={dayBounds?.max}
            onChange={e => setDate(e.target.value)}
            className="tnum w-full bg-transparent text-sm text-foam outline-none [color-scheme:dark]"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="eyebrow flex shrink-0 items-center gap-1.5">
          {t('context.species')}
          <HintDot id="species" />
        </span>
        {/* «Все виды» — валидный выбор: модели по виду тогда ждут выбора, снос
            показывает маркеры всех видов, «Карты» вид игнорируют. */}
        <div className="min-w-0 flex-1">
          <SpeciesSelector
            species={inView}
            outOfView={outOfView}
            value={species}
            onChange={setSpecies}
            allowAll
          />
        </div>
      </div>

      {caveat && (
        <p
          role="note"
          className="rounded-lg border border-buoy/40 bg-buoy/10 px-2.5 py-2 text-[11px] leading-snug text-buoy"
        >
          {caveat}
        </p>
      )}

      {!depthApplicable && depthNote && (
        <p className="text-[11px] leading-snug text-faint">{t(depthNote)}</p>
      )}

      {depthApplicable && (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <span className="eyebrow flex items-center gap-1.5">
              {t('context.depth')}
              <HintDot id="depth" />
            </span>
            <span className="text-[10px] font-medium tracking-[0.1em] text-faint">
              {t('context.probe')}
            </span>
          </div>
          <DepthSliderCompact depth={depth} onChange={setDepth} />
        </div>
      )}
    </div>
  )
}
